import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function CapitalEfficiency() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader unnumbered eyebrow="Appendix D · Capital Efficiency">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          What this would have cost in 2022.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          AI-native development is the arbitrage.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-4 md:flex-1 md:grid-cols-3 md:gap-5">
        {/* Card 1 — What we've built */}
        <div className="flex flex-col rounded-lg bg-slate-50 p-5 ring-1 ring-slate-200">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            Built
          </p>
          <p className="mt-3 text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
            Production-scale, real-time.
          </p>
          <ul className="mt-3 flex-1 space-y-2 text-sm leading-relaxed text-slate-700">
            <Stat>
              <span className="font-semibold text-slate-900">880K</span> lines
              of code ·{" "}
              <span className="font-semibold text-slate-900">4,350</span>{" "}
              source files
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">1,429</span> test
              files ·{" "}
              <span className="font-semibold text-slate-900">15,749</span> unit
              tests
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">10</span>{" "}
              proprietary ASO engines
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">4</span> daily
              intelligence streams (audit · competitor · AI/web · store)
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">45+</span> backend
              services
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">22+</span>{" "}
              external integrations
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">13</span> GitHub
              Actions test workflows
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">3</span> cloud
              platforms (GCP · Cloudflare · GitHub)
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">5</span>{" "}
              customer-facing surfaces (marketing · SaaS app · admin · docs ·
              blog)
            </Stat>
          </ul>
        </div>

        {/* Card 2 — 2019 Equivalent */}
        <div className="flex flex-col rounded-lg bg-slate-50 p-5 ring-1 ring-slate-200">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            2022 Equivalent
          </p>
          <p className="mt-0.5 text-[10px] italic text-slate-400">
            Pre-GPT-4
          </p>
          <p className="mt-3 text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
            $22–25M raised. 26 FTE. 24 months.
          </p>
          <ul className="mt-3 flex-1 space-y-2 text-sm leading-relaxed text-slate-700">
            <Stat>
              <span className="font-semibold text-slate-900">$14.3M</span> burn
              to GA
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">$2.5–3.5M</span> ML
              pod alone — no LLM APIs, bespoke NLP
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">24 months</span> to
              production
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">26 FTE</span>:
              4-pod org (FE / BE / ML / Platform)
            </Stat>
            <Stat>
              <span className="font-semibold text-slate-900">$8–9M</span>{" "}
              post-GA runway
            </Stat>
          </ul>
          <p className="mt-3 text-[11px] italic text-slate-500">
            Per internal build-cost analysis, 2026-06-05.
          </p>
        </div>

        {/* Card 3 — Apptonomy Arbitrage (highlighted) */}
        <div className="flex flex-col rounded-lg bg-blue-50 p-5 ring-2 ring-blue-400">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
            Apptonomy Arbitrage
          </p>
          <p className="mt-3 text-lg font-semibold tracking-tight text-slate-900 md:text-xl">
            We're early. They can't catch up cheaply.
          </p>
          <div className="mt-3 flex flex-1 flex-col gap-3">
            <Beat title="AI-native by design">
              Cursor, Claude Code, frontier LLM APIs, and agentic workflows
              woven through every engine, every workflow, every release.
            </Beat>
            <Beat title="Incumbents can't pivot">
              AppTweak, Sensor Tower — legacy codebases. Adopting these
              patterns is a multi-year rebuild against customers paying for
              the old dashboard.
            </Beat>
            <Beat title="New entrants can't ship the system">
              Thin wrappers on a single frontier model. No production
              engineering DNA, no integrations, no orchestration.
            </Beat>
          </div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
            20+ years at production scale × AI-native execution
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className="text-blue-600">
        ·
      </span>
      <span>{children}</span>
    </li>
  );
}

function Beat({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md bg-white p-3 ring-1 ring-blue-200">
      <p className="text-xs font-bold uppercase tracking-[0.12em] text-blue-700">
        {title}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-slate-700">{children}</p>
    </div>
  );
}
